<script setup lang="ts">
    import { computed, onMounted, nextTick, shallowReactive, watch, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import PortfolioScenarioNavigation from '@Quest/PortfolioScenarios/Components/PortfolioScenarioNavigation.vue'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import { ProjectsGridViewModel } from '@Quest/Projects/Components/ProjectsGrid'
    import { ReviewPageReadModel } from '@Quest/PortfolioScenarios/readmodels'

    import { useReviewPageQueryV1 } from '@Quest/PortfolioScenarios/queries-vue-composables'
    import { useSaveReviewGridCommandV1 } from '@Quest/PortfolioScenarios/commands-vue-composables'


    const route = useRoute()
    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))

    const {
        runReviewPageQueryV1,
        modelReviewPageQueryV1
    } = useReviewPageQueryV1(workspaceId, tchProjectId, scenarioId)

    const rm = modelReviewPageQueryV1 as Ref<ReviewPageReadModel | null>
    const portfolioId = computed(() => rm.value?.PortfolioId)

    const grid = shallowReactive(new ProjectsGridViewModel())

    // ======================
    // Commands
    // ======================

    const {
        invokeSaveReviewGridCommandV1,
        onSucceedSaveReviewGridCommandV1
    } = useSaveReviewGridCommandV1(workspaceId, portfolioId, scenarioId)

    // ======================
    // Handlers
    // ======================

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveReviewGridCommand()
    {
        const gridJson = grid.getJson()
        invokeSaveReviewGridCommandV1({ Grid: gridJson })
    }

    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    function onSelected(result: any)
    {
        // intentionally empty (same as legacy)
    }


    onMounted(() =>
    {
        document.title = "Portfolio Plan - Review"
        runReviewPageQueryV1()
    })


    watch(rm, (current) =>
    {
        if (!current) return

        nextTick(() =>
        {
            grid.renderGrid(
                current.Grid.IdField,
                current.Grid.Fields,
                current.Grid.DefaultFieldIds,
                current.Grid.Options
            )

            grid.setProjects(current.Projects.map(p => p.Properties))
        })
    })

    onSucceedSaveReviewGridCommandV1(() =>
    {
        alert("Saved")
    })
</script>

<template>
    <div>
        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">{{rm.PortfolioName}} / {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Plan: Review</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <PortfolioScenarioNavigation :portfolioId="rm.PortfolioId" 
                                                 :corporateScenarioUrl="rm.CorporateScenarioUrl"
                                                 @selected="onSelected"/>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="alert alert-warning" role="alert">
                <p class="mb-0">
                    Please note that project-level data in this report is based on the start date in
                    <strong>Kanban</strong> & <strong>Active Business Case</strong>.
                </p>
            </div>

            <div class="toolbar">
                <div>
                    <!--<button type="button" class="btn btn-outline-secondary">Action ({{selectedIds.length}})</button>-->
                </div>
                <div>

                    <!-- Edit + Save View Group -->
                    <div class="btn-group mr-2">
                        <button type="button" class="btn btn-outline-secondary" @click="handleShowGridColumnsEditor">
                            Edit columns
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="handleSaveReviewGridCommand" title="Save current grid layout and filters">
                            Save view
                        </button>
                    </div>

                    <!-- Actions Dropdown -->
                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <button class="dropdown-item" @click="handleExportGrid">
                                <i class="fa fa-download fa-sm mr-1"></i> Download to spreadsheet
                            </button>
                            <button class="dropdown-item" @click="handleClearGridFilters">
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ProjectsGrid :viewModel="grid" />

        </div>
    </div>

</template>

<style scoped>
    th { 
        text-align: center; 
    }

    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }

</style>
